import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const C = {
  primary:   [59, 130, 246],
  darkBg:    [30, 64, 175],
  lightBg:   [239, 246, 255],
  textDark:  [17, 24, 39],
  textGray:  [107, 114, 128],
  white:     [255, 255, 255],
  rowAlt:    [249, 250, 251],
  footerBg:  [243, 244, 246],
};

const PRIORITY_COLORS = {
  P1: [220, 38, 38],
  P2: [234, 88, 12],
  P3: [161, 98, 7],
  P4: [21, 128, 61],
};

const STATUS_COLORS = {
  'Completed':   [21, 128, 61],
  'In Progress': [29, 78, 216],
  'In Review':   [109, 40, 217],
  'Blocked':     [185, 28, 28],
  'Planning':    [161, 98, 7],
  'On Hold':     [154, 52, 18],
  'Cancelled':   [107, 114, 128],
  'Not Started': [156, 163, 175],
};

const formatDate = (str) => {
  if (!str) return '';
  const d = new Date(str + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const drawHeader = (doc, dateRange, employeeName) => {
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;

  doc.setFillColor(...C.darkBg);
  doc.rect(0, 0, pageW, 32, 'F');
  doc.setFillColor(...C.primary);
  doc.rect(0, 28, pageW, 5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...C.white);
  doc.text('Weekly Status Report', margin, 13);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(196, 219, 254);
  doc.text(`${formatDate(dateRange.start)}  –  ${formatDate(dateRange.end)}`, margin, 21);
  doc.text(`Employee: ${employeeName}`, margin, 27);

  const genDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.text(`Generated ${genDate}`, pageW - margin, 27, { align: 'right' });
};

const drawSummaryCards = (doc, summary, y) => {
  const margin = 14;
  const pageW = doc.internal.pageSize.getWidth();
  const contentW = pageW - margin * 2;
  const cards = [
    { label: 'TOTAL HOURS',      value: String(summary.totalHours) },
    { label: 'TASKS WORKED ON',  value: String(summary.tasksTouchedCount) },
    { label: 'STATUS UPDATES',   value: String(summary.statusUpdatesCount) },
  ];
  const gap = 4;
  const cardW = (contentW - gap * 2) / 3;

  cards.forEach((card, i) => {
    const x = margin + i * (cardW + gap);

    doc.setFillColor(...C.lightBg);
    doc.roundedRect(x, y, cardW, 22, 2, 2, 'F');
    doc.setDrawColor(...C.primary);
    doc.setLineWidth(0.4);
    doc.roundedRect(x, y, cardW, 22, 2, 2, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(...C.primary);
    doc.text(card.value, x + cardW / 2, y + 12, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.textGray);
    doc.text(card.label, x + cardW / 2, y + 19, { align: 'center' });
  });

  return y + 30;
};

const drawPriorityChart = (doc, tasks, y) => {
  const margin = 14;
  const pageW = doc.internal.pageSize.getWidth();
  const contentW = pageW - margin * 2;

  const priorityHours = { P1: 0, P2: 0, P3: 0, P4: 0 };
  tasks.forEach(t => {
    if (t.priority && priorityHours[t.priority] !== undefined) {
      priorityHours[t.priority] += t.hoursThisWeek;
    }
  });

  const maxHours = Math.max(...Object.values(priorityHours), 0.1);
  const barAreaW = contentW - 28;
  const barH = 6;
  const rowH = 10;
  const labelW = 14;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(...C.textDark);
  doc.text('Hours by Priority', margin, y);
  y += 5;

  Object.entries(priorityHours).forEach(([p, hrs], idx) => {
    const rowY = y + idx * rowH;
    const barW = (hrs / maxHours) * barAreaW;
    const color = PRIORITY_COLORS[p] || [156, 163, 175];

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.textDark);
    doc.text(p, margin, rowY + barH - 1);

    doc.setFillColor(229, 231, 235);
    doc.roundedRect(margin + labelW, rowY, barAreaW, barH, 1, 1, 'F');

    if (barW > 0.5) {
      doc.setFillColor(...color);
      doc.roundedRect(margin + labelW, rowY, barW, barH, 1, 1, 'F');
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.textGray);
    doc.text(`${hrs.toFixed(1)}h`, margin + labelW + barAreaW + 2, rowY + barH - 1);
  });

  return y + Object.keys(priorityHours).length * rowH + 6;
};

const drawStatusDistribution = (doc, tasks, y) => {
  const margin = 14;
  const pageW = doc.internal.pageSize.getWidth();
  const contentW = pageW - margin * 2;

  const statusCount = {};
  tasks.forEach(t => {
    const s = t.status || 'Unknown';
    statusCount[s] = (statusCount[s] || 0) + 1;
  });

  if (Object.keys(statusCount).length === 0) return y;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(...C.textDark);
  doc.text('Task Status Distribution', margin, y);
  y += 4;

  const total = Object.values(statusCount).reduce((a, b) => a + b, 0);
  const entries = Object.entries(statusCount).sort((a, b) => b[1] - a[1]);
  const cols = 3;
  const colW = contentW / cols;

  entries.forEach(([status, count], idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const x = margin + col * colW;
    const itemY = y + row * 7;
    const color = STATUS_COLORS[status] || [156, 163, 175];
    const pct = ((count / total) * 100).toFixed(0);

    doc.setFillColor(...color);
    doc.circle(x + 2, itemY + 2, 2, 'F');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.textDark);
    doc.text(`${status}: ${count} (${pct}%)`, x + 6, itemY + 3.5);
  });

  const rows = Math.ceil(entries.length / cols);
  return y + rows * 7 + 6;
};

const drawTasksTable = (doc, tasks, y) => {
  const margin = 14;
  const pageW = doc.internal.pageSize.getWidth();
  const contentW = pageW - margin * 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(...C.textDark);
  doc.text('Task Summary', margin, y);
  y += 2;

  const totalHours = tasks.reduce((s, t) => s + t.hoursThisWeek, 0);

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: contentW,
    head: [['Task', 'Project / Client', 'Priority', 'Status', 'Hours']],
    body: [
      ...tasks.map(t => [
        t.title,
        t.project || '—',
        t.priority || '—',
        t.status || '—',
        t.hoursThisWeek.toFixed(2),
      ]),
      ['', '', '', 'TOTAL', totalHours.toFixed(2)],
    ],
    styles:          { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak' },
    columnStyles: {
      0: { cellWidth: 68 },
      1: { cellWidth: 40 },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 28, halign: 'center' },
      4: { cellWidth: 'auto', halign: 'right' },
    },
    headStyles:      { fillColor: C.primary, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: C.rowAlt },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const isTotal = data.row.index === tasks.length;
        if (isTotal) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = C.lightBg;
        }
        if (data.column.index === 2 && !isTotal) {
          const color = PRIORITY_COLORS[data.cell.raw];
          if (color) { data.cell.styles.textColor = color; data.cell.styles.fontStyle = 'bold'; }
        }
        if (data.column.index === 3 && !isTotal) {
          const color = STATUS_COLORS[data.cell.raw];
          if (color) data.cell.styles.textColor = color;
        }
      }
    },
  });

  return doc.lastAutoTable.finalY + 8;
};

const drawUpdatesTable = (doc, tasks, startY) => {
  const margin = 14;
  const pageW = doc.internal.pageSize.getWidth();
  const contentW = pageW - margin * 2;
  let y = startY;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(...C.textDark);
  doc.text('Daily Status Updates', margin, y);
  y += 2;

  const body = [];
  tasks.forEach(task => {
    task.updates.forEach(update => {
      body.push([task.title, update.date, String(update.timeSpentHours), update.statusText || '']);
    });
  });

  if (body.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...C.textGray);
    doc.text('No updates in this period.', margin, y + 5);
    return;
  }

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: contentW,
    head: [['Task', 'Date', 'Hrs', 'Update']],
    body,
    styles:          { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak' },
    columnStyles: {
      0: { cellWidth: 52 },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 10, halign: 'center' },
      3: { cellWidth: 'auto' },
    },
    headStyles:      { fillColor: C.primary, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: C.rowAlt },
  });
};

const addFooters = (doc) => {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFillColor(...C.footerBg);
    doc.rect(0, pageH - 9, pageW, 9, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...C.textGray);
    doc.text('Top3 Statuses — Confidential', margin, pageH - 3);
    doc.text(`Page ${i} of ${total}`, pageW - margin, pageH - 3, { align: 'right' });
  }
};

export const exportReportToPdf = (report, dateRange, employeeName = 'All Employees') => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageH = doc.internal.pageSize.getHeight();

  drawHeader(doc, dateRange, employeeName);

  let y = 40;
  y = drawSummaryCards(doc, report.summary, y);

  const touchedTasks = (report.tasks || []).filter(t => t.touched);

  y = drawPriorityChart(doc, touchedTasks, y);
  y = drawStatusDistribution(doc, touchedTasks, y);

  if (y > pageH - 50) { doc.addPage(); y = 14; }
  y = drawTasksTable(doc, touchedTasks, y);

  if (y > pageH - 40) { doc.addPage(); y = 14; }
  drawUpdatesTable(doc, touchedTasks, y);

  addFooters(doc);

  doc.save(`weekly-report-${dateRange.start}-to-${dateRange.end}.pdf`);
};
